import { useState, useEffect, useCallback } from 'react';
import { Copy, Link2, Loader2, Check, Globe, Lock, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { IconButton } from '@/components/ui/icon-button';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import type { ReceiptShare } from '@/lib/types';

interface ReceiptShareDialogProps {
  receiptId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  includeAmount: boolean;
  onIncludeAmountChange: (v: boolean) => void;
  includeItems: boolean;
  onIncludeItemsChange: (v: boolean) => void;
  includeNotes: boolean;
  onIncludeNotesChange: (v: boolean) => void;
  includeMerchant: boolean;
  onIncludeMerchantChange: (v: boolean) => void;
}

const EXPIRY_OPTIONS = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

// BUG-FE1: backend expects expiresIn as a number (hours), not a string like '7d'
const EXPIRY_TO_HOURS: Record<string, number> = {
  '1h': 1,
  '24h': 24,
  '3d': 72,
  '7d': 168,
  '30d': 720,
};

function relativeExpiry(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.round(diff / (1000 * 60 * 60));
  if (hours < 24) return `Expires in ${hours}h`;
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  return `Expires in ${days} day${days !== 1 ? 's' : ''}`;
}

export default function ReceiptShareDialog({
  receiptId,
  open,
  onOpenChange,
  includeAmount,
  onIncludeAmountChange,
  includeItems,
  onIncludeItemsChange,
  includeNotes,
  onIncludeNotesChange,
  includeMerchant,
  onIncludeMerchantChange,
}: ReceiptShareDialogProps) {
  const { toast } = useToast();

  const [creating, setCreating] = useState(false);
  const [expiresIn, setExpiresIn] = useState('7d');
  const [maxAccesses, setMaxAccesses] = useState('');

  const [createdShare, setCreatedShare] = useState<ReceiptShare | null>(null);
  const [existingShares, setExistingShares] = useState<ReceiptShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);

  const fetchExistingShares = useCallback(async () => {
    setLoadingShares(true);
    try {
      const res = await api.get('/receipts/shares', { params: { receiptId } });
      const data = res.data.data || res.data;
      setExistingShares(Array.isArray(data) ? data : data.shares || []);
    } catch {
      /* ignore */
    }
    setLoadingShares(false);
  }, [receiptId]);

  useEffect(() => {
    if (open) {
      fetchExistingShares();
      setCreatedShare(null);
      setCopied(false);
    }
  }, [open, fetchExistingShares]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const body = {
        receiptId,
        // BUG-FE1: send number (hours), not string like '7d'
        expiresIn: EXPIRY_TO_HOURS[expiresIn] ?? 72,
        maxAccesses: maxAccesses ? parseInt(maxAccesses, 10) : undefined,
        privacySettings: { includeAmount, includeItems, includeNotes, includeMerchant },
      };
      const res = await api.post('/receipts/shares', body);
      const raw = res.data.data || res.data;
      // BUG-FE3: normalize id → _id so filter/delete lookups work
      const share: ReceiptShare = {
        ...raw,
        _id: raw._id || raw.id,
        accessCount: raw.accessCount ?? 0,
      };
      setCreatedShare(share);
      setExistingShares((prev) => [share, ...prev]);
      toast({ title: 'Share link created' });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        toast({ title: 'Invalid share settings. Please check your inputs.', variant: 'destructive' });
      } else if (status === 403) {
        toast({ title: "You don't have permission to share this receipt.", variant: 'destructive' });
      } else if (status === 404) {
        toast({ title: 'This receipt no longer exists.', variant: 'destructive' });
      } else {
        toast({ title: 'Failed to create share link', variant: 'destructive' });
      }
    }
    setCreating(false);
  };

  const getShareUrl = (share: ReceiptShare) => {
    if (share.shareUrl) return share.shareUrl;
    return `${window.location.origin}/shared/${share.token}`;
  };

  const handleCopy = async (share: ReceiptShare) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(share));
      setCopied(true);
      toast({ title: 'Link copied!' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const handleDelete = async (shareId: string) => {
    try {
      await api.delete(`/receipts/shares/${shareId}`);
      setExistingShares((prev) => prev.filter((s) => s._id !== shareId));
      if (createdShare?._id === shareId) setCreatedShare(null);
      toast({ title: 'Share link revoked' });
    } catch {
      toast({ title: 'Failed to revoke share', variant: 'destructive' });
    }
    setRevokeTarget(null);
  };

  const privacySummary = [
    !includeAmount && 'Amount hidden',
    !includeItems && 'Items hidden',
    !includeNotes && 'Notes hidden',
    !includeMerchant && 'Merchant hidden',
  ].filter(Boolean).join(' · ');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="receipt-share-dialog">
          <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" /> Share Receipt
          </DialogTitle>
          <DialogDescription>Create a shareable link for this receipt</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Settings */}
          {!createdShare && (
            <>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Expiration</Label>
                  <Select value={expiresIn} onValueChange={setExpiresIn}>
                    <SelectTrigger className="mt-1 min-h-[44px]" data-testid="share-expiry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">
                    Max accesses{' '}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={maxAccesses}
                    onChange={(e) => setMaxAccesses(e.target.value)}
                    placeholder="Unlimited"
                    className="mt-1 min-h-[44px]"
                    data-testid="share-max-accesses"
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <Lock className="h-4 w-4" /> Privacy Settings
                  </p>
                  {[
                    {
                      label: 'Include amount',
                      checked: includeAmount,
                      onChange: onIncludeAmountChange,
                      testId: 'share-include-amount',
                    },
                    {
                      label: 'Include items',
                      checked: includeItems,
                      onChange: onIncludeItemsChange,
                      testId: 'share-include-items',
                    },
                    {
                      label: 'Include notes',
                      checked: includeNotes,
                      onChange: onIncludeNotesChange,
                      testId: 'share-include-notes',
                    },
                    {
                      label: 'Include merchant',
                      checked: includeMerchant,
                      onChange: onIncludeMerchantChange,
                      testId: 'share-include-merchant',
                    },
                  ].map((toggle) => (
                    <div key={toggle.testId} className="flex items-center justify-between">
                      <Label className="text-sm">{toggle.label}</Label>
                      <Switch
                        checked={toggle.checked}
                        onCheckedChange={toggle.onChange}
                        data-testid={toggle.testId}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Button
                className="w-full h-11 min-h-[44px]"
                onClick={handleCreate}
                disabled={creating}
                data-testid="share-generate"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Globe className="mr-2 h-4 w-4" /> Generate Link
                  </>
                )}
              </Button>
            </>
          )}

          {/* Created share result */}
          {createdShare && (
            <div
              className="space-y-3 rounded-lg border bg-muted/30 p-4"
              data-testid="share-result"
            >
              <p className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                <Check className="h-4 w-4" /> Link created!
              </p>
              {privacySummary && (
                <p className="text-xs text-muted-foreground" data-testid="share-privacy-summary">
                  {privacySummary}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={getShareUrl(createdShare)}
                  className="flex-1 font-mono text-xs"
                  data-testid="share-url-input"
                />
                <IconButton
                  tooltip="Copy link"
                  variant="outline"
                  className="min-h-[44px] min-w-[44px] shrink-0"
                  onClick={() => handleCopy(createdShare)}
                  data-testid="share-copy"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </IconButton>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                onClick={() => setCreatedShare(null)}
              >
                Create another link
              </Button>
            </div>
          )}

          {/* Existing shares — BUG-FE4: filter out createdShare to avoid duplicate */}
          {existingShares.filter((s) => s._id !== createdShare?._id).length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Active shares</p>
                {loadingShares ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  existingShares
                    .filter((s) => s._id !== createdShare?._id)
                    .map((share) => (
                      <div
                        key={share._id}
                        className="flex items-center gap-2 rounded border p-2 text-xs"
                        data-testid={`share-item-${share._id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-mono">{getShareUrl(share)}</p>
                          <p className="text-muted-foreground">
                            {relativeExpiry(share.expiresAt)} ·{' '}
                            {share.accessCount} access{share.accessCount !== 1 ? 'es' : ''}
                            {share.maxAccesses ? ` / ${share.maxAccesses}` : ''}
                          </p>
                        </div>
                        {/* BUG-FE6: min-h-[44px] via IconButton */}
                        <IconButton
                          tooltip="Copy link"
                          variant="ghost"
                          className="min-h-[44px] min-w-[44px] shrink-0"
                          onClick={() => handleCopy(share)}
                          data-testid={`share-copy-btn-${share._id}`}
                        >
                          <Copy className="h-3 w-3" />
                        </IconButton>
                        {/* BUG-FE5: trigger AlertDialog instead of direct delete */}
                        <IconButton
                          tooltip="Revoke"
                          variant="ghost"
                          className="min-h-[44px] min-w-[44px] shrink-0 text-destructive"
                          onClick={() => setRevokeTarget(share._id)}
                          data-testid={`share-revoke-btn-${share._id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </IconButton>
                      </div>
                    ))
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* BUG-FE5: Revoke confirmation */}
    <AlertDialog open={revokeTarget !== null} onOpenChange={(o) => !o && setRevokeTarget(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke share link?</AlertDialogTitle>
          <AlertDialogDescription>
            Anyone with this link will lose access immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => revokeTarget && handleDelete(revokeTarget)}
            data-testid="share-revoke-confirm"
          >
            Revoke
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
